import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';
import { onAnimationEnd } from '@theme/utilities';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 * @property {HTMLElement} [mainNav] - The main navigation element.
 * @property {HTMLElement[]} [submenus] - Array of submenu elements.
 * @property {HTMLElement[]} [carets] - Array of caret icons.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details'];

  /** @type {string | null} */
  #activeSubmenu = null;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keyup', this.#onKeyUp);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
  }

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    // If a submenu is open, close it first
    if (this.#activeSubmenu) {
      this.closeSubmenu({ handle: this.#activeSubmenu });
      return;
    }

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {Event} [event]
   */
  open(event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');
    requestAnimationFrame(() => {
      details.classList.add('menu-open');
      requestAnimationFrame(() => {
        trapFocus(details);
      });
    });
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    // Close any open submenu first
    if (this.#activeSubmenu) {
      this.#resetSubmenu(this.#activeSubmenu);
    }
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');

    onAnimationEnd(details, () => {
      reset(details);

      if (details === this.refs.details) {
        removeTrapFocus();
        const openDetails = this.querySelectorAll('details[open]');
        openDetails.forEach(reset);
      } else {
        requestAnimationFrame(() => {
          trapFocus(this.refs.details);
        });
      }
    });
  }

  /**
   * Open a submenu by handle
   * @param {{ handle: string }} data - The submenu handle
   * @param {Event} [event]
   */
  openSubmenu(data, event) {
    event?.preventDefault();

    const { handle } = data;
    const submenu = this.#getSubmenu(handle);
    const mainNav = this.refs.mainNav;

    if (!submenu || !mainNav) return;

    // Check if submenu has children
    if (!submenu.querySelector('ul li')) return;

    this.#activeSubmenu = handle;

    // Hide carets immediately for visual feedback
    this.#setCaretsOpacity(0);

    // Add class to main nav to slide it out
    mainNav.classList.add('submenu-active');

    // Show submenu using CSS classes (no forced reflow!)
    submenu.classList.add('show');
    submenu.setAttribute('aria-hidden', 'false');

    // Focus management after transition
    submenu.addEventListener(
      'transitionend',
      () => {
        const firstLink = submenu.querySelector('a, button');
        if (firstLink instanceof HTMLElement) {
          firstLink.focus();
        }
      },
      { once: true }
    );
  }

  /**
   * Close a submenu by handle
   * @param {{ handle: string }} data - The submenu handle
   * @param {Event} [event]
   */
  closeSubmenu(data, event) {
    event?.preventDefault();

    const { handle } = data;
    this.#resetSubmenu(handle);
  }

  /**
   * Reset submenu state
   * @param {string} handle
   */
  #resetSubmenu(handle) {
    const submenu = this.#getSubmenu(handle);
    const mainNav = this.refs.mainNav;

    if (!submenu || !mainNav) return;

    this.#activeSubmenu = null;

    // Hide submenu
    submenu.classList.remove('show');
    submenu.setAttribute('aria-hidden', 'true');

    // Show main nav
    mainNav.classList.remove('submenu-active');

    // Show carets after transition completes
    submenu.addEventListener(
      'transitionend',
      () => {
        this.#setCaretsOpacity(1);

        // Return focus to the trigger button
        const trigger = this.querySelector(`[data-submenu-trigger="${handle}"]`);
        if (trigger instanceof HTMLElement) {
          trigger.focus();
        }
      },
      { once: true }
    );
  }

  /**
   * Get a submenu element by handle
   * @param {string} handle
   * @returns {HTMLElement | null}
   */
  #getSubmenu(handle) {
    return this.querySelector(`[data-submenu="${handle}"]`);
  }

  /**
   * Set opacity on all caret icons
   * @param {number} opacity
   */
  #setCaretsOpacity(opacity) {
    const carets = this.refs.carets;
    if (!carets) return;

    for (const caret of carets) {
      caret.style.opacity = String(opacity);
    }
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
